import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    fetchAnchorConfig,
    fetchComponentCombinations,
    fetchCountries,
    fetchCountrySummary,
    fetchFilterCounts,
    fetchQueryEstimate,
    fetchRates,
    fetchStats,
    fetchSubMarkets,
    queryBudgetEstimateFromError,
    coverageIncompleteFromError,
} from "../api";
import {
    ActiveFilters,
    ALL_SALES_CHANNELS,
    AnchorSelector,
    ComponentSection,
    CountrySummary,
    DistributionChart,
    FilterBar,
    FramesDetails,
    LoadingSpinner,
    QueryBudgetNotice,
    CoverageNotice,
    StatsCard,
    TabNav,
    type FilterValue,
    type Tab,
} from "../components";
import {
    useApi,
    getComponentQueue,
    getDashboardQueue,
    useSharedParams,
    useLiveSearchParams,
    ALL_FRAME_TYPES,
} from "../hooks";
import { useIsMobile } from "../hooks/useIsMobile";
import type { CrossFilters, DistributionItem, Stats } from "../types";
import { formatColourFilterValue } from "../utils/colourLabels";
import { exclusiveEndDate, isHeavyPeriod, parseTertialDates, periodStartDate } from "../utils/dates";
import { buildFrontTypeDistribution, frontTypeDistributionFromCombos, frontTypesQueryParam } from "../utils/frontTypes";
import { formatSelection, splitSelection, togglePieSelection } from "../utils/pieSelection";
import { canLoadParquetQueries, isFrameTypesReady, shouldDeferHeavySecondary } from "../utils/loadGating";
import { shouldPrefetchTab } from "../utils/loadGating";
import { componentFamilyNames, withFamilies } from "../utils/componentFamilies";
import styles from "./Dashboard.module.css";

const DOOR_SOLUTION_LABELS: Record<string, string> = {
    no_doors: "No Doors",
    hinge_door: "Hinged Door",
    sliding_door: "Sliding Door",
    mixed: "Mixed",
};

const DOOR_SOLUTION_API_VALUES: Record<string, string> = Object.fromEntries(
    Object.entries(DOOR_SOLUTION_LABELS).map(([k, v]) => [v, k])
);

/** Format attach rate for tab badges; preserves 0% (unlike truthy checks). */
function formatAttachBadge(rate?: { percentage: number }): string | undefined {
    if (!rate) return undefined;
    return `${rate.percentage.toFixed(0)}%`;
}

type ComponentTabType =
    | "fronts"
    | "doors"
    | "interior"
    | "loose_interior"
    | "lighting"
    | "knobs_handles"
    | "legs"
    | "top_panels"
    | "eket"
    | "drawer_fronts";

const OWN_TAB_CROSS_KEYS: Record<ComponentTabType, readonly string[]> = {
    fronts: ["door_family", "door_item"],
    doors: ["door_family", "door_item"],
    interior: ["interior_family", "interior_product_type", "interior_color", "interior_item"],
    loose_interior: [
        "loose_interior_family",
        "loose_interior_product_type",
        "loose_interior_color",
        "loose_interior_item",
    ],
    lighting: ["lighting_family", "lighting_product_type", "lighting_color", "lighting_item"],
    knobs_handles: ["knobs_family", "knobs_product_type", "knobs_color", "knobs_item"],
    legs: ["legs_family", "legs_product_type", "legs_color", "legs_item"],
    top_panels: [
        "top_panels_family",
        "top_panels_product_type",
        "top_panels_color",
        "top_panels_item",
    ],
    // EKET drilldown is self-contained (its own tab only); no cross-tab
    // filtering of other tabs by EKET attributes yet.
    eket: [],
    // Drawer fronts (BESTÅ) is self-contained; no cross-tab filtering yet.
    drawer_fronts: [],
};

/** Cross-tab drill-down for component-combinations (omits own-tab keys). */
function crossTabDrilldownFor(
    tab: ComponentTabType,
    drilldown: Record<string, string | undefined>
): Record<string, string | undefined> {
    const omit = new Set(OWN_TAB_CROSS_KEYS[tab]);
    return Object.fromEntries(
        Object.entries(drilldown).filter(
            ([key, value]) => !omit.has(key) && value !== undefined && value !== ""
        )
    );
}

const FRONT_TYPE_LABELS: Record<string, string> = {
    doors: "Doors",
    drawer_fronts: "Drawer fronts",
    mixed: "Mixed",
};

function parseFrontTypes(value?: string | null): string[] {
    if (!value) return [];
    return value
        .split(",")
        .map((entry) => entry.trim().toLowerCase().replace(/\s+/g, "_"))
        .filter((entry) => entry && Object.prototype.hasOwnProperty.call(FRONT_TYPE_LABELS, entry));
}

function frontTypeLabelForValue(value: string): string {
    return FRONT_TYPE_LABELS[value] ?? value;
}

const RUNNER_TYPE_LABELS: Record<string, string> = {
    push_open: "Push-open",
    soft_closing: "Soft-closing",
    mixed: "Mixed",
    no_runner: "No runner",
};

function parseRunnerTypes(value?: string | null): string[] {
    if (!value) return [];
    return value
        .split(",")
        .map((entry) => entry.trim().toLowerCase().replace(/[\s-]+/g, "_"))
        .filter((entry) => entry && Object.prototype.hasOwnProperty.call(RUNNER_TYPE_LABELS, entry));
}

function runnerTypeValueForLabel(label: string): string {
    return Object.entries(RUNNER_TYPE_LABELS).find(([, display]) => display === label)?.[0] ?? label;
}

function runnerTypeLabelForValue(value: string): string {
    return RUNNER_TYPE_LABELS[value] ?? value;
}

const FAVORITE_COUNTRY_KEY = "mcc-favorite-country";
const FAVORITE_PERIOD_KEY = "mcc-favorite-period";

function getStored(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function setStored(key: string, value: string | null): void {
    try {
        if (value) {
            localStorage.setItem(key, value);
        } else {
            localStorage.removeItem(key);
        }
    } catch {
        /* ignore */
    }
}

export function Dashboard() {
    const [searchParams, setSearchParams] = useLiveSearchParams();
    const { anchor, selectedCountries, selectedSalesChannels, subMarketsParam, period, frameTypes, homeCountryResolved, countriesFromUrl } =
        useSharedParams(ALL_SALES_CHANNELS);
    const [favoriteCountry, setFavoriteCountry] = useState<string | null>(() => getStored(FAVORITE_COUNTRY_KEY));
    const [favoritePeriod, setFavoritePeriod] = useState<string | null>(() => getStored(FAVORITE_PERIOD_KEY));
    const isMobile = useIsMobile();
    const [filtersOpen, setFiltersOpen] = useState(true);

    // Fetch per-anchor config to drive dynamic tabs/filters
    const { data: anchorConfig } = useApi(() => fetchAnchorConfig(anchor), [anchor], true);
    const anchorConfigReady = anchorConfig?.anchor === anchor;
    const anchorFlags = useMemo(() => new Set(anchorConfig?.feature_flags || []), [anchorConfig]);
    // The Front types (Doors/Drawer fronts/Mixed) pie only means something for
    // anchors that can have both — doors-only anchors like PAX would just show
    // a trivial "100% Doors" slice. For those, show the Door Solutions
    // (Hinged/Sliding/Mixed) pie instead.
    const showDoorSolutionsPie = anchorFlags.has("has_doors") && !anchorFlags.has("has_drawer_fronts");
    // Doors-only anchors (PAX) never had a "fronts" concept pre-merge — keep
    // the tab and its headings labelled "Doors" for them; anchors with both
    // doors and drawer fronts (BESTÅ) keep the merged "Fronts" label.
    const frontsTabLabel = showDoorSolutionsPie ? "Doors" : "Fronts";
    const frontsTitleBase = showDoorSolutionsPie ? "Door" : "Front";

    // Auto-correct period when it becomes disabled after anchor switch
    const dataAvailableFrom = anchorConfig?.data_available_from || undefined;
    useEffect(() => {
        if (!dataAvailableFrom || period === "custom" || period.startsWith("custom:")) return;
        const startDate = periodStartDate(period);
        if (startDate && startDate < dataAvailableFrom) {
            // Current period is disabled — fall back to largest available
            const fallbacks = ["4w", "1w"];
            const fallback =
                fallbacks.find((p) => {
                    const s = periodStartDate(p);
                    return !s || s >= dataAvailableFrom;
                }) || "1w";
            setSearchParams((prev) => {
                prev.set("period", fallback);
                return prev;
            });
        }
    }, [dataAvailableFrom, period, setSearchParams]);

    // Parse custom/tertial period into start_date/end_date, or use preset period
    const periodParams = useMemo(() => {
        if (period.startsWith("custom:")) {
            const [, start, end] = period.split(":");
            return { start_date: start, end_date: exclusiveEndDate(end) };
        }
        const tertial = parseTertialDates(period);
        if (tertial) return tertial;
        return { period };
    }, [period]);

    // Handle favorite changes
    const handleFavoriteChange = useCallback((country: string | null) => {
        setFavoriteCountry(country);
        setStored(FAVORITE_COUNTRY_KEY, country);
    }, []);
    const handleFavoritePeriodChange = useCallback((p: string | null) => {
        setFavoritePeriod(p);
        setStored(FAVORITE_PERIOD_KEY, p);
    }, []);

    // Cross-filter values from URL
    const crossFilters: CrossFilters = useMemo(
        () => ({
            color: searchParams.get("color") || undefined,
            height_cm: searchParams.get("height_cm") || undefined,
            depth_cm: searchParams.get("depth_cm") || undefined,
            width_cm: searchParams.get("width_cm") ? parseInt(searchParams.get("width_cm")!, 10) : undefined,
        }),
        [searchParams]
    );

    // Door color filter (separate from frame color)
    const doorColor = searchParams.get("door_color") || undefined;

    // Component color filters
    const interiorColor = searchParams.get("interior_color") || undefined;
    const looseInteriorColor = searchParams.get("loose_interior_color") || undefined;
    const knobsColor = searchParams.get("knobs_color") || undefined;
    const lightingColor = searchParams.get("lighting_color") || undefined;
    const legsColor = searchParams.get("legs_color") || undefined;
    const topPanelsColor = searchParams.get("top_panels_color") || undefined;
    const topPanelSize = searchParams.get("top_panel_size") || undefined;
    const topPanelKind = searchParams.get("top_panel_kind") || undefined;
    const eketColor = searchParams.get("eket_color") || undefined;
    const drawerFrontsColor = searchParams.get("drawer_fronts_color") || undefined;

    // Component family filters
    const interiorFamily = searchParams.get("interior_family") || undefined;
    const interiorProductType = searchParams.get("interior_product_type") || undefined;
    const looseInteriorFamily = searchParams.get("loose_interior_family") || undefined;
    const looseInteriorProductType = searchParams.get("loose_interior_product_type") || undefined;
    const knobsFamily = searchParams.get("knobs_family") || undefined;
    const knobsProductType = searchParams.get("knobs_product_type") || undefined;
    const lightingFamily = searchParams.get("lighting_family") || undefined;
    const lightingProductType = searchParams.get("lighting_product_type") || undefined;
    const legsFamily = searchParams.get("legs_family") || undefined;
    const legsProductType = searchParams.get("legs_product_type") || undefined;
    const topPanelsFamily = searchParams.get("top_panels_family") || undefined;
    const topPanelsProductType = searchParams.get("top_panels_product_type") || undefined;
    const eketFamily = searchParams.get("eket_family") || undefined;
    const eketProductType = searchParams.get("eket_product_type") || undefined;
    const drawerFrontsFamily = searchParams.get("drawer_fronts_family") || undefined;
    const drawerFrontsProductType = searchParams.get("drawer_fronts_product_type") || undefined;
    const doorFamily = searchParams.get("door_family") || undefined;

    // Door solution filter
    const doorSolution = searchParams.get("door_solution") || undefined;

    // Selected item descriptions (survive tab switches via URL params)
    const doorItem = searchParams.get("door_item") || undefined;
    const interiorItem = searchParams.get("interior_item") || undefined;
    const looseInteriorItem = searchParams.get("loose_interior_item") || undefined;
    const knobsItem = searchParams.get("knobs_item") || undefined;
    const lightingItem = searchParams.get("lighting_item") || undefined;
    const legsItem = searchParams.get("legs_item") || undefined;
    const topPanelsItem = searchParams.get("top_panels_item") || undefined;
    const eketItem = searchParams.get("eket_item") || undefined;
    const drawerFrontsItem = searchParams.get("drawer_fronts_item") || undefined;

    // Track whether door_family was set by a row click (vs independently via dropdown)
    const doorFamilyFromItemRef = useRef(false);
    // Track whether door_color was set by a row click (vs independently via dropdown)
    const doorColorFromItemRef = useRef(false);

    // Frame type filter — always send resolved value to API.
    // For anchors without configured frame_type_categories, the filter is
    // meaningless: don't send it to the API and don't show it as an active
    // filter pill. anchorConfig is guaranteed loaded by the early return above.
    const hasFrameTypeCategories = !anchorConfig || anchorConfig.frame_type_categories.length > 0;
    // When frame_types from URL don't match the current anchor's categories
    // (e.g. after switching from PAX to BESTÅ), use all categories (no filter).
    const effectiveFrameTypes = useMemo(() => {
        if (!anchorConfig || anchorConfig.frame_type_categories.length === 0) return frameTypes ?? ALL_FRAME_TYPES;
        if (!frameTypes) return anchorConfig.default_frame_types ?? anchorConfig.frame_type_categories;
        const valid = frameTypes.filter((t) => anchorConfig.frame_type_categories.includes(t));
        return valid.length > 0 ? valid : (anchorConfig.default_frame_types ?? anchorConfig.frame_type_categories);
    }, [frameTypes, anchorConfig]);
    const frameTypesForApi = hasFrameTypeCategories ? effectiveFrameTypes.join(",") || undefined : undefined;

    // Tab state from URL (default to "frames")
    const requestedTab = searchParams.get("tab") || "frames";
    const activeTab =
        requestedTab === "doors" || requestedTab === "drawer-fronts" ? "fronts" : requestedTab;

    useEffect(() => {
        if (requestedTab === "doors" || requestedTab === "drawer-fronts") {
            setSearchParams((prev) => {
                prev.set("tab", "fronts");
                return prev;
            });
        }
    }, [requestedTab, setSearchParams]);

    const frontTypeSelection = useMemo(() => {
        const selected = parseFrontTypes(searchParams.get("front_types"));
        return selected.length > 0 ? selected : ["doors", "drawer_fronts", "mixed"];
    }, [searchParams]);

    const runnerTypeSelection = useMemo(() => {
        return parseRunnerTypes(searchParams.get("runner_types"));
    }, [searchParams]);
    const runnerTypePieSelection = useMemo(() => {
        return runnerTypeSelection.length > 0
            ? runnerTypeSelection.map(runnerTypeLabelForValue).join(",")
            : undefined;
    }, [runnerTypeSelection]);
    const runnerTypesForApi = runnerTypeSelection.length > 0 ? runnerTypeSelection.join(",") : undefined;
    const handleRunnerTypeClick = useCallback(
        (labelStr: string) => {
            const labels = labelStr
                ? labelStr
                      .split(",")
                      .map((l) => l.trim())
                      .filter(Boolean)
                : [];
            const values = labels
                .map(
                    (label) =>
                        Object.entries(RUNNER_TYPE_LABELS).find(([, display]) => display === label)?.[0]
                )
                .filter((v): v is string => Boolean(v));
            setSearchParams((prev) => {
                if (values.length === 0) {
                    prev.delete("runner_types");
                } else {
                    prev.set("runner_types", values.join(","));
                }
                return prev;
            });
        },
        [setSearchParams]
    );

    const frontTypePieSelection = useMemo(() => {
        return frontTypeSelection.length === 3 ? undefined : frontTypeSelection.map(frontTypeLabelForValue).join(",");
    }, [frontTypeSelection]);
    const frontTypesForApi = frontTypesQueryParam(frontTypeSelection);
    const frontColorFilter =
        frontTypeSelection.length === 1 && frontTypeSelection[0] === "drawer_fronts"
            ? drawerFrontsColor
            : doorColor || drawerFrontsColor;
    const frontFamilyFilter =
        frontTypeSelection.length === 1 && frontTypeSelection[0] === "drawer_fronts"
            ? drawerFrontsFamily
            : doorFamily || drawerFrontsFamily;

    const handleFrontTypeClick = useCallback(
        (label: string, multiSelect: boolean) => {
            const value = Object.entries(FRONT_TYPE_LABELS).find(([, display]) => display === label)?.[0] ?? label;
            const current = searchParams.has("front_types") ? frontTypeSelection : [];
            const next = togglePieSelection(current, value, Object.keys(FRONT_TYPE_LABELS), multiSelect);
            setSearchParams((prev) => {
                if (next.length === 0) prev.delete("front_types");
                else prev.set("front_types", next.join(","));
                return prev;
            });
        },
        [frontTypeSelection, searchParams, setSearchParams]
    );

    // Door Solutions pie (Hinged/Sliding/Mixed) — shown in place of the trivial
    // "100% Doors" Front types pie for doors-only anchors (e.g. PAX), which have
    // no drawer fronts to split against. Mirrors handleFrontTypeClick's
    // single/multi-select toggle, but drives the pre-existing door_solution
    // filter param (already threaded through the /v1/stats fetches above).
    const selectedDoorSolutionLabel = doorSolution
        ? doorSolution
              .split(",")
              .map((v) => DOOR_SOLUTION_LABELS[v.trim()])
              .filter(Boolean)
              .join(",")
        : undefined;
    // Full (unfiltered) door-solution split, populated further down.
    const [fullDoorSolutions, setFullDoorSolutions] = useState<{ label: string; value: number }[]>([]);
    const handleDoorSolutionClick = useCallback(
        (label: string, multiSelect: boolean) => {
            const apiValue = DOOR_SOLUTION_API_VALUES[label] || "";
            if (!apiValue) return;
            const allValues = fullDoorSolutions
                .map((d) => DOOR_SOLUTION_API_VALUES[d.label])
                .filter((v): v is string => Boolean(v));
            const next = togglePieSelection(splitSelection(doorSolution), apiValue, allValues, multiSelect);
            setSearchParams((prev) => {
                if (next.length === 0) prev.delete("door_solution");
                else prev.set("door_solution", next.join(","));
                return prev;
            });
        },
        [doorSolution, fullDoorSolutions, setSearchParams]
    );

    // Fetch countries for dropdowns.
    // Countries doubles as a warmup probe — once it returns, the backend is alive.
    const { data: countries } = useApi(() => fetchCountries(anchor), [anchor]);
    const backendReady = !!countries;
    // If the selected country isn't available for the current anchor, pick the
    // first available country so we don't fire queries that 404.
    useEffect(() => {
        if (!countries || countries.length === 0) return;
        // Only wait for the home country to resolve when the selection came from
        // the home-country/favorite/DE default (no explicit URL param) —
        // otherwise a late-resolving home country would be clobbered by "first
        // available". When the URL carries an explicit (invalid) countries
        // param, home country is irrelevant, so heal immediately instead of
        // waiting up to the /auth/me timeout and firing 404s in the meantime.
        if (!countriesFromUrl && !homeCountryResolved) return;
        const valid = selectedCountries.filter((c) => countries.includes(c));
        if (valid.length === 0) {
            setSearchParams((prev) => {
                prev.delete("country");
                prev.set("countries", countries[0]);
                return prev;
            });
        } else if (valid.length !== selectedCountries.length) {
            setSearchParams((prev) => {
                prev.delete("country");
                prev.set("countries", valid.join(","));
                return prev;
            });
        }
    }, [countries, selectedCountries, setSearchParams, homeCountryResolved, countriesFromUrl]);

    // Fetch sub markets for all selected countries
    const { data: subMarkets, error: subMarketsError } = useApi(
        () => fetchSubMarkets(selectedCountries.join(",")),
        [selectedCountries.join(",")],
        selectedCountries.length > 0
    );

    // Compute selected sub markets - if param is empty and we have sub markets, select all.
    // If the sub-markets endpoint fails, treat as "no filter" so the dashboard isn't blocked.
    const allSubMarketCodes = subMarkets?.map((s) => s.code) || [];
    const selectedSubMarkets =
        subMarketsParam !== null
            ? subMarketsParam.split(",").filter((code) => allSubMarketCodes.includes(code))
            : allSubMarketCodes;

    // Fetch stats and combinations based on filters (including cross-filters, door solution, and door color)
    // Join selected countries with comma for API
    const countriesForApi = selectedCountries.join(",");
    // Join selected sales channels with comma for API (only pass if not all selected)
    const salesChannelsForApi =
        selectedSalesChannels.length === ALL_SALES_CHANNELS.length ? undefined : selectedSalesChannels.join(",");
    // Join selected sub markets with comma for API (only pass if not all selected)
    const subMarketsForApi =
        selectedSubMarkets.length === allSubMarketCodes.length ? undefined : selectedSubMarkets.join(",");

    // Hooks still run while the early return below shows a spinner — so we must
    // not mark filtersReady until frame defaults are known. Otherwise the first
    // stats call uses ALL_FRAME_TYPES and a second call uses default (regular).
    const frameTypesReady = isFrameTypesReady(frameTypes, anchorConfigReady);
    const selectionReady =
        backendReady &&
        selectedCountries.length > 0 &&
        selectedSalesChannels.length > 0 &&
        (selectedSubMarkets.length > 0 || !!subMarketsError) &&
        frameTypesReady &&
        effectiveFrameTypes.length > 0;

    const queryEstimateKey = `${anchor}:${countriesForApi}:${period}`;
    const {
        data: queryEstimateResult,
        loading: queryEstimateLoading,
        error: queryEstimateError,
    } = useApi(
        () =>
            fetchQueryEstimate({
                anchor,
                country: countriesForApi,
                ...periodParams,
            }).then((estimate) => ({ key: queryEstimateKey, estimate })),
        [queryEstimateKey],
        selectionReady
    );
    const queryEstimate = queryEstimateResult?.key === queryEstimateKey ? queryEstimateResult.estimate : null;
    const queryBudgetBlocked = selectionReady && !queryEstimateLoading && queryEstimate?.allowed === false;
    const queryBudgetUnavailable = selectionReady && !queryEstimateLoading && !!queryEstimateError;
    const filtersReady = canLoadParquetQueries(
        selectionReady,
        queryEstimateLoading,
        queryEstimate?.allowed,
        queryEstimateKey,
        queryEstimateResult?.key
    );

    const heavyPeriod = useMemo(() => isHeavyPeriod(period), [period]);
    const dashboardQueue = useMemo(() => getDashboardQueue(heavyPeriod), [heavyPeriod]);
    // Key of filters that reset the heavy-period "core first" gate. Must stay in
    // lockstep with the useEffect that sets coreLoaded true after stats+rates.
    const heavyFilterKey = useMemo(
        () =>
            [
                anchor,
                countriesForApi,
                period,
                subMarketsForApi ?? "",
                salesChannelsForApi ?? "",
                frameTypesForApi ?? "",
            ].join("|"),
        [anchor, countriesForApi, period, subMarketsForApi, salesChannelsForApi, frameTypesForApi]
    );
    const [coreLoaded, setCoreLoaded] = useState(!heavyPeriod);
    const [seenHeavyFilterKey, setSeenHeavyFilterKey] = useState(heavyFilterKey);
    // Reset coreLoaded in the *same render* as a filter change so heavyDefer is
    // true before useApi effects run. A post-paint useEffect was too late: the
    // country/period change already re-enabled filter-counts for one spurious
    // request (same URL as the real deferred call → double hit, 2nd from cache).
    if (heavyFilterKey !== seenHeavyFilterKey) {
        setSeenHeavyFilterKey(heavyFilterKey);
        setCoreLoaded(!heavyPeriod);
    }
    const heavyDefer = shouldDeferHeavySecondary(heavyPeriod, coreLoaded);

    // Component drill-down filters shared by /v1/rates and /v1/stats so tab
    // badges and KPI cards stay in sync when users filter within a component tab.
    const componentDrilldownParams = useMemo(
        () => ({
            door_family: doorFamily,
            interior_family: interiorFamily,
            lighting_family: lightingFamily,
            knobs_family: knobsFamily,
            loose_interior_family: looseInteriorFamily,
            legs_family: legsFamily,
            top_panels_family: topPanelsFamily,
            interior_product_type: interiorProductType,
            interior_color: interiorColor,
            interior_item: interiorItem,
            loose_interior_product_type: looseInteriorProductType,
            loose_interior_color: looseInteriorColor,
            loose_interior_item: looseInteriorItem,
            lighting_product_type: lightingProductType,
            lighting_color: lightingColor,
            lighting_item: lightingItem,
            knobs_product_type: knobsProductType,
            knobs_color: knobsColor,
            knobs_item: knobsItem,
            legs_product_type: legsProductType,
            legs_color: legsColor,
            legs_item: legsItem,
            top_panels_product_type: topPanelsProductType,
            top_panels_color: topPanelsColor,
            top_panels_item: topPanelsItem,
            door_item: doorItem,
        }),
        [
            doorFamily,
            interiorFamily,
            lightingFamily,
            knobsFamily,
            looseInteriorFamily,
            legsFamily,
            topPanelsFamily,
            interiorProductType,
            interiorColor,
            interiorItem,
            looseInteriorProductType,
            looseInteriorColor,
            looseInteriorItem,
            lightingProductType,
            lightingItem,
            lightingColor,
            knobsProductType,
            knobsColor,
            knobsItem,
            legsProductType,
            legsColor,
            legsItem,
            topPanelsProductType,
            topPanelsColor,
            topPanelsItem,
            doorItem,
        ]
    );

    // Fetch cross-filtered dropdown counts (Looker-style)
    const { data: filterCounts, loading: filterCountsLoading } = useApi(
        () =>
            fetchFilterCounts({
                anchor,
                country: countriesForApi || undefined,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                frame_types: frameTypesForApi,
                ...periodParams,
            }),
        [anchor, countriesForApi, salesChannelsForApi, subMarketsForApi, period, frameTypesForApi],
        filtersReady && !heavyDefer,
        { queue: dashboardQueue, queueId: "filter-counts", queuePriority: 20 }
    );

    // Stagger all data fetches to avoid overwhelming the backend.
    // Light requests (countries, sub markets) go first, then heavier parquet queries.
    const {
        data: stats,
        loading: statsLoading,
        error: statsError,
    } = useApi(
        () =>
            fetchStats({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                ...crossFilters,
                door_solution: doorSolution,
                door_color: doorColor,
                frame_types: frameTypesForApi,
                door_family: doorFamily,
                interior_family: interiorFamily,
                lighting_family: lightingFamily,
                knobs_family: knobsFamily,
                interior_product_type: interiorProductType,
                interior_color: interiorColor,
                interior_item: interiorItem,
                loose_interior_product_type: looseInteriorProductType,
            }),
        [
            anchor,
            countriesForApi,
            salesChannelsForApi,
            subMarketsForApi,
            period,
            crossFilters.color,
            crossFilters.height_cm,
            crossFilters.depth_cm,
            crossFilters.width_cm,
            doorSolution,
            doorColor,
            frameTypesForApi,
            doorFamily,
            interiorFamily,
            lightingFamily,
            knobsFamily,
            interiorProductType,
            interiorColor,
            interiorItem,
            looseInteriorProductType,
        ],
        filtersReady,
        { queue: dashboardQueue, queueId: "stats", queuePriority: 0 }
    );
    const requestBudgetEstimate = queryBudgetEstimateFromError(statsError);
    const displayedBudgetEstimate = queryBudgetBlocked ? queryEstimate : requestBudgetEstimate;
    // Coverage-incomplete is recoverable (deselect the named countries or pick a
    // later period), so — like the budget prompt — keep the dashboard and its
    // FilterBar rendered instead of collapsing to the no-data message.
    const coverageError = coverageIncompleteFromError(statsError);

    // Unfiltered frame type distribution — the backend computes it in the same
    // stats scan (frame_type_distribution_unfiltered), so the pie chart and
    // frame-type badges always show all segments with real values even when a
    // frame_types filter is active, with no second /v1/stats call. Falls back to
    // the filtered distribution for older backends that omit the field.
    const unfilteredFrameTypes = useMemo<Stats["frame_type_distribution"]>(
        () =>
            stats?.frame_type_distribution_unfiltered?.length
                ? stats.frame_type_distribution_unfiltered
                : (stats?.frame_type_distribution ?? []),
        [stats?.frame_type_distribution_unfiltered, stats?.frame_type_distribution]
    );

    // Build frame type category counts from unfiltered stats (for MultiSelect badges)
    const PRODUCT_TYPE_TO_CATEGORY: Record<string, string> = {
        // PAX
        "wardrobe frame": "regular",
        "wall-mounted storage frame": "regular",
        "wall-mounted storage frame w rail": "regular",
        "add-on corner unit": "corner",
        "add-on corner unit with 4 shelves": "corner",
        "corner section frame": "corner",
        "add-on end-unit": "end",
        "add-on mid-unit": "mid",
        // BESTÅ
        frame: "regular",
        "shelf unit": "regular",
        "shelf unit/height extension unit": "regular",
        "TV bench": "tv",
        "TV storage unit": "tv",
        "wall shelf": "regular",
        "wall shelf unit": "regular",
    };
    const frameTypeCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const d of unfilteredFrameTypes) {
            const cat = PRODUCT_TYPE_TO_CATEGORY[d.frame_type] || d.frame_type;
            counts[cat] = (counts[cat] || 0) + d.count;
        }
        return Object.keys(counts).length > 0 ? counts : undefined;
    }, [unfilteredFrameTypes]);

    // Pie charts react to width + door/corner filters but NOT to their own
    // dimensions (color, height, depth).  The width table is also a selector
    // that always shows all widths.
    //
    // pieBase: stats filtered by width + door/corner but NOT color/height/depth.
    // Used by pie charts when their own dimension is filtered.
    const hasPieCrossFilter = !!(crossFilters.color || crossFilters.height_cm || crossFilters.depth_cm);
    const [pieDistributions, setPieDistributions] = useState<{
        color: DistributionItem[];
        height: DistributionItem[];
        depth: DistributionItem[];
    }>({ color: [], height: [], depth: [] });
    const [pieFetched, setPieFetched] = useState(false);
    // Reset cache when non-pie params change (including width)
    useEffect(() => {
        setPieFetched(false);
        setPieDistributions({ color: [], height: [], depth: [] });
    }, [
        anchor,
        countriesForApi,
        salesChannelsForApi,
        subMarketsForApi,
        period,
        doorSolution,
        doorColor,
        frameTypesForApi,
        crossFilters.width_cm,
        interiorProductType,
    ]);
    const { data: pieBaseStats } = useApi(
        () =>
            fetchStats({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                width_cm: crossFilters.width_cm,
                door_solution: doorSolution,
                door_color: doorColor,
                frame_types: frameTypesForApi,
                ...(interiorProductType === "No fixed interior" ? { has_interior: false } : {}),
            }),
        [
            anchor,
            countriesForApi,
            salesChannelsForApi,
            subMarketsForApi,
            period,
            crossFilters.width_cm,
            doorSolution,
            doorColor,
            frameTypesForApi,
            interiorProductType,
        ],
        filtersReady && hasPieCrossFilter && !pieFetched && !heavyDefer
    );
    useEffect(() => {
        if (pieBaseStats) {
            setPieDistributions({
                color: pieBaseStats.color_distribution ?? [],
                height: pieBaseStats.height_distribution ?? [],
                depth: pieBaseStats.depth_distribution ?? [],
            });
            setPieFetched(true);
        }
    }, [pieBaseStats]);
    // When no pie cross-filter, update from live stats
    useEffect(() => {
        if (stats && !hasPieCrossFilter) {
            setPieDistributions({
                color: stats.color_distribution ?? [],
                height: stats.height_distribution ?? [],
                depth: stats.depth_distribution ?? [],
            });
            setPieFetched(false);
        }
    }, [stats, hasPieCrossFilter]);

    // Width table: shows all widths with the selected one highlighted.
    // Same pattern as pie charts: when the user's own dimension is filtered,
    // do a dedicated fetch WITHOUT that filter so the full distribution stays
    // visible.  Other cross-filters (color, height, depth, …) still apply.
    const [fullWidthDistribution, setFullWidthDistribution] = useState<DistributionItem[]>([]);
    const [widthFetched, setWidthFetched] = useState(false);
    // Reset cache when any non-width param changes
    useEffect(() => {
        setWidthFetched(false);
        setFullWidthDistribution([]);
    }, [
        anchor,
        countriesForApi,
        salesChannelsForApi,
        subMarketsForApi,
        period,
        crossFilters.color,
        crossFilters.height_cm,
        crossFilters.depth_cm,
        doorSolution,
        doorColor,
        frameTypesForApi,
        interiorProductType,
    ]);
    const { data: widthBaseStats } = useApi(
        () =>
            fetchStats({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                color: crossFilters.color,
                height_cm: crossFilters.height_cm,
                depth_cm: crossFilters.depth_cm,
                door_solution: doorSolution,
                door_color: doorColor,
                frame_types: frameTypesForApi,
                ...(interiorProductType === "No fixed interior" ? { has_interior: false } : {}),
            }),
        [
            anchor,
            countriesForApi,
            salesChannelsForApi,
            subMarketsForApi,
            period,
            crossFilters.color,
            crossFilters.height_cm,
            crossFilters.depth_cm,
            doorSolution,
            doorColor,
            frameTypesForApi,
            interiorProductType,
        ],
        filtersReady && !!crossFilters.width_cm && !widthFetched && !heavyDefer
    );
    useEffect(() => {
        if (widthBaseStats) {
            setFullWidthDistribution(widthBaseStats.width_distribution ?? []);
            setWidthFetched(true);
        }
    }, [widthBaseStats]);
    // When no width is selected, use live stats directly
    useEffect(() => {
        if (stats && !crossFilters.width_cm) {
            setFullWidthDistribution(stats.width_distribution ?? []);
            setWidthFetched(false);
        }
    }, [stats, crossFilters.width_cm]);

    // Fetch country summary for the selection panel
    const { data: countrySummary, loading: countrySummaryLoading } = useApi(
        () =>
            fetchCountrySummary({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
            }),
        [anchor, countriesForApi, salesChannelsForApi, subMarketsForApi, period],
        filtersReady && !heavyDefer,
        { queue: dashboardQueue, queueId: "country-summary", queuePriority: 15 }
    );

    // Lightweight rates for all component types (single fast request)
    const {
        data: rates,
        loading: ratesLoading,
        error: ratesError,
        refetch: ratesRefetch,
    } = useApi(
        () =>
            fetchRates({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                ...crossFilters,
                door_solution: doorSolution,
                door_color: doorColor,
                frame_types: frameTypesForApi,
                ...componentDrilldownParams,
            }),
        [
            anchor,
            countriesForApi,
            salesChannelsForApi,
            subMarketsForApi,
            period,
            crossFilters.color,
            crossFilters.height_cm,
            crossFilters.depth_cm,
            crossFilters.width_cm,
            doorSolution,
            doorColor,
            frameTypesForApi,
            componentDrilldownParams,
        ],
        filtersReady,
        { queue: dashboardQueue, queueId: "rates", queuePriority: 5 }
    );

    useEffect(() => {
        if (heavyPeriod && stats && rates) {
            setCoreLoaded(true);
        }
    }, [heavyPeriod, stats, rates]);

    // Fetch component combinations (doors, interior, lighting, knobs & handles)
    // These are deferred until the user opens the relevant tab.
    // Shared deps = frame cross-filters + full cross-tab drill-down (via componentDrilldownParams).
    // Tab-specific deps = own-tab filters sent as component_* (not cross-tab keys).
    const sharedComponentDeps = [
        anchor,
        countriesForApi,
        salesChannelsForApi,
        subMarketsForApi,
        period,
        crossFilters.color,
        crossFilters.height_cm,
        crossFilters.depth_cm,
        crossFilters.width_cm,
        frameTypesForApi,
        doorColor,
        doorSolution,
        componentDrilldownParams,
    ];
    const frontsDeps = [
        ...sharedComponentDeps,
        frontTypesForApi,
        runnerTypesForApi,
        frontColorFilter,
        frontFamilyFilter,
        drawerFrontsProductType,
    ];
    const interiorDeps = [
        ...sharedComponentDeps,
        interiorColor,
        interiorProductType,
        interiorItem,
    ];
    const looseInteriorDeps = [
        ...sharedComponentDeps,
        looseInteriorColor,
        looseInteriorProductType,
        looseInteriorItem,
    ];
    const lightingDeps = [...sharedComponentDeps, lightingProductType, lightingColor, lightingItem];
    const knobsDeps = [...sharedComponentDeps, knobsColor, knobsProductType, knobsItem];
    const legsDeps = [...sharedComponentDeps, legsColor, legsProductType, legsItem];
    const topPanelsDeps = [
        ...sharedComponentDeps,
        topPanelsColor,
        topPanelSize,
        topPanelKind,
        topPanelsProductType,
        topPanelsItem,
    ];
    const eketDeps = [...sharedComponentDeps, eketColor, eketProductType, eketItem];
    // Concurrent request queue: component types fire in parallel once enabled,
    // spreading across backend workers. Priority still respected for ordering.
    const queue = useMemo(
        () => (heavyPeriod ? dashboardQueue : getComponentQueue()),
        [heavyPeriod, dashboardQueue]
    );
    const queuePriority = (tab: string) => (tab === activeTab ? 0 : 10);

    // On initial load, defer non-active component tabs until the active tab's
    // data has arrived.  This avoids overwhelming a cold backend with parallel
    // requests the user hasn't asked for yet.
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const isActiveTab = useCallback((tab: string) => tab === activeTab, [activeTab]);
    // On heavy periods (e.g. FY), never speculatively prefetch inactive tabs:
    // each component-combinations scan is minutes long. Only the active tab loads;
    // other tabs fetch lazily on click. Light periods keep the prefetch-all behaviour.
    const tabEnabled = useCallback(
        (tab: string) => shouldPrefetchTab(isActiveTab(tab), heavyPeriod, initialLoadComplete),
        [isActiveTab, initialLoadComplete, heavyPeriod]
    );

    // When the user switches tabs, boost the new active tab's queued request
    useEffect(() => {
        const tabToQueueId: Record<string, string> = {
            fronts: "fronts",
            interior: "interior",
            lighting: "lighting",
            "knobs-handles": "knobs_handles",
            legs: "legs",
            "top-panels": "top_panels",
            eket: "eket",
        };
        const queueId = tabToQueueId[activeTab];
        if (queueId) queue.prioritize(queueId, 0);
    }, [activeTab, queue]);

    const {
        data: frontCombos,
        loading: frontsLoading,
        error: frontsError,
        refetch: frontsRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "fronts",
                front_types: frontTypesForApi,
                runner_types: runnerTypesForApi,
                ...crossFilters,
                ...crossTabDrilldownFor("fronts", componentDrilldownParams),
                component_color: frontColorFilter,
                component_family: frontFamilyFilter,
                component_product_type:
                    frontTypeSelection.length === 1 && frontTypeSelection[0] === "drawer_fronts"
                        ? drawerFrontsProductType
                        : undefined,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 20,
                with_qty_page_size: 20,
            }),
        frontsDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 ||
                anchorFlags.has("has_doors") ||
                anchorFlags.has("has_drawer_fronts")) &&
            tabEnabled("fronts"),
        { queue, queueId: "fronts", queuePriority: queuePriority("fronts") }
    );
    useEffect(() => {
        if (frontCombos?.door_solution_distribution) {
            setFullDoorSolutions(
                frontCombos.door_solution_distribution.map((item) => ({
                    label: item.description,
                    value: item.count,
                }))
            );
        }
    }, [frontCombos?.door_solution_distribution]);

    const {
        data: interiorCombos,
        loading: interiorLoading,
        error: interiorError,
        refetch: interiorRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "interior",
                ...crossFilters,
                ...crossTabDrilldownFor("interior", componentDrilldownParams),
                component_color: interiorColor,
                component_family: interiorFamily,
                component_product_type: interiorProductType,
                component_item: interiorItem,
                door_color: doorColor,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 15,
                with_qty_page_size: 15,
            }),
        interiorDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 || anchorFlags.has("has_interior")) &&
            tabEnabled("interior"),
        { queue, queueId: "interior", queuePriority: queuePriority("interior") }
    );

    const {
        data: looseInteriorCombos,
        loading: looseInteriorLoading,
        error: looseInteriorError,
        refetch: looseInteriorRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "loose_interior",
                ...crossFilters,
                ...crossTabDrilldownFor("loose_interior", componentDrilldownParams),
                component_color: looseInteriorColor,
                component_family: looseInteriorFamily,
                component_product_type: looseInteriorProductType,
                component_item: looseInteriorItem,
                door_color: doorColor,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 15,
                with_qty_page_size: 15,
            }),
        looseInteriorDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 || anchorFlags.has("has_interior")) &&
            tabEnabled("loose-interior"),
        { queue, queueId: "loose_interior", queuePriority: queuePriority("loose-interior") }
    );

    const {
        data: lightingCombos,
        loading: lightingLoading,
        error: lightingError,
        refetch: lightingRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "lighting",
                ...crossFilters,
                ...crossTabDrilldownFor("lighting", componentDrilldownParams),
                component_color: lightingColor,
                component_family: lightingFamily,
                component_product_type: lightingProductType,
                component_item: lightingItem,
                door_color: doorColor,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 15,
                with_qty_page_size: 15,
            }),
        lightingDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 || anchorFlags.has("has_lighting")) &&
            tabEnabled("lighting"),
        { queue, queueId: "lighting", queuePriority: queuePriority("lighting") }
    );

    const {
        data: knobsCombos,
        loading: knobsLoading,
        error: knobsError,
        refetch: knobsRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "knobs_handles",
                ...crossFilters,
                ...crossTabDrilldownFor("knobs_handles", componentDrilldownParams),
                component_color: knobsColor,
                component_family: knobsFamily,
                component_product_type: knobsProductType,
                knobs_item: knobsItem,
                door_color: doorColor,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 15,
                with_qty_page_size: 15,
            }),
        knobsDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 || anchorFlags.has("has_knobs_handles")) &&
            tabEnabled("knobs-handles"),
        { queue, queueId: "knobs_handles", queuePriority: queuePriority("knobs-handles") }
    );

    const {
        data: legsCombos,
        loading: legsLoading,
        error: legsError,
        refetch: legsRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "legs",
                ...crossFilters,
                ...crossTabDrilldownFor("legs", componentDrilldownParams),
                component_color: legsColor,
                component_family: legsFamily,
                component_product_type: legsProductType,
                component_item: legsItem,
                door_color: doorColor,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 15,
                with_qty_page_size: 15,
            }),
        legsDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 || anchorFlags.has("has_legs")) &&
            tabEnabled("legs"),
        { queue, queueId: "legs", queuePriority: queuePriority("legs") }
    );

    const {
        data: topPanelsCombos,
        loading: topPanelsLoading,
        error: topPanelsError,
        refetch: topPanelsRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "top_panels",
                ...crossFilters,
                ...crossTabDrilldownFor("top_panels", componentDrilldownParams),
                component_color: topPanelsColor,
                top_panel_size: topPanelSize,
                top_panel_kind: topPanelKind,
                component_family: topPanelsFamily,
                component_product_type: topPanelsProductType,
                component_item: topPanelsItem,
                door_color: doorColor,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 15,
                with_qty_page_size: 15,
            }),
        topPanelsDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 || anchorFlags.has("has_top_panel")) &&
            tabEnabled("top-panels"),
        { queue, queueId: "top_panels", queuePriority: queuePriority("top-panels") }
    );

    const {
        data: eketCombos,
        loading: eketLoading,
        error: eketError,
        refetch: eketRefetch,
    } = useApi(
        () =>
            fetchComponentCombinations({
                anchor,
                country: countriesForApi,
                sub_market: subMarketsForApi,
                sales_channels: salesChannelsForApi,
                ...periodParams,
                component_type: "eket",
                ...crossFilters,
                ...crossTabDrilldownFor("eket", componentDrilldownParams),
                component_color: eketColor,
                component_family: eketFamily,
                component_product_type: eketProductType,
                component_item: eketItem,
                door_color: doorColor,
                door_solution: doorSolution,
                frame_types: frameTypesForApi,
                generic_page_size: 15,
                with_qty_page_size: 15,
            }),
        eketDeps,
        filtersReady &&
            anchorConfigReady &&
            (anchorFlags.size === 0 || anchorFlags.has("has_eket")) &&
            tabEnabled("eket"),
        { queue, queueId: "eket", queuePriority: queuePriority("eket") }
    );

    const handleFrontColorClick = useCallback(
        (value: string) => {
            const selected = new Set(frontTypeSelection);
            const target = selected.has("drawer_fronts") && !selected.has("doors") ? "drawer_fronts" : "doors";
            if (target === "drawer_fronts") {
                setSearchParams((prev) => {
                    if (!value) prev.delete("drawer_fronts_color");
                    else prev.set("drawer_fronts_color", value);
                    return prev;
                });
            } else {
                setSearchParams((prev) => {
                    if (!value) prev.delete("door_color");
                    else prev.set("door_color", value);
                    return prev;
                });
            }
        },
        [frontTypeSelection, setSearchParams]
    );

    const handleFrontFamilyClick = useCallback(
        (value: string) => {
            const selected = new Set(frontTypeSelection);
            const target = selected.has("drawer_fronts") && !selected.has("doors") ? "drawer_fronts" : "doors";
            if (target === "drawer_fronts") {
                setSearchParams((prev) => {
                    if (!value) prev.delete("drawer_fronts_family");
                    else prev.set("drawer_fronts_family", value);
                    return prev;
                });
            } else {
                setSearchParams((prev) => {
                    if (!value) prev.delete("door_family");
                    else prev.set("door_family", value);
                    return prev;
                });
            }
        },
        [frontTypeSelection, setSearchParams]
    );

    // Once the active tab's data arrives, enable all remaining tabs
    useEffect(() => {
        if (initialLoadComplete) return;
        const activeData =
            activeTab === "frames"
                ? stats
                : activeTab === "fronts"
                  ? frontCombos
                  : activeTab === "interior"
                    ? interiorCombos
                    : activeTab === "loose-interior"
                      ? looseInteriorCombos
                      : activeTab === "lighting"
                        ? lightingCombos
                        : activeTab === "knobs-handles"
                          ? knobsCombos
                          : activeTab === "legs"
                            ? legsCombos
                            : activeTab === "top-panels"
                              ? topPanelsCombos
                              : activeTab === "eket"
                                ? eketCombos
                                : stats;
        if (activeData) setInitialLoadComplete(true);
    }, [
        activeTab,
        initialLoadComplete,
        stats,
        frontCombos,
        interiorCombos,
        looseInteriorCombos,
        lightingCombos,
        knobsCombos,
        legsCombos,
        topPanelsCombos,
        eketCombos,
    ]);

    // Base params for server-side pagination (components use these for page fetches)
    const frontsFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "fronts" as const,
            front_types: frontTypesForApi,
            runner_types: runnerTypesForApi,
            ...crossFilters,
            ...crossTabDrilldownFor("fronts", componentDrilldownParams),
            component_color: frontColorFilter,
            component_family: frontFamilyFilter,
            component_product_type:
                frontTypeSelection.length === 1 && frontTypeSelection[0] === "drawer_fronts"
                    ? drawerFrontsProductType
                    : undefined,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        frontsDeps
    );
    const interiorFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "interior" as const,
            ...crossFilters,
            ...crossTabDrilldownFor("interior", componentDrilldownParams),
            component_color: interiorColor,
            component_family: interiorFamily,
            component_product_type: interiorProductType,
            component_item: interiorItem,
            door_color: doorColor,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        interiorDeps
    );
    const looseInteriorFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "loose_interior" as const,
            ...crossFilters,
            ...crossTabDrilldownFor("loose_interior", componentDrilldownParams),
            component_color: looseInteriorColor,
            component_family: looseInteriorFamily,
            component_product_type: looseInteriorProductType,
            component_item: looseInteriorItem,
            door_color: doorColor,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        looseInteriorDeps
    );
    const lightingFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "lighting" as const,
            ...crossFilters,
            ...crossTabDrilldownFor("lighting", componentDrilldownParams),
            component_color: lightingColor,
            component_family: lightingFamily,
            component_product_type: lightingProductType,
            component_item: lightingItem,
            door_color: doorColor,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        lightingDeps
    );
    const knobsFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "knobs_handles" as const,
            ...crossFilters,
            ...crossTabDrilldownFor("knobs_handles", componentDrilldownParams),
            component_color: knobsColor,
            component_family: knobsFamily,
            component_product_type: knobsProductType,
            knobs_item: knobsItem,
            door_color: doorColor,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        knobsDeps
    );
    const legsFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "legs" as const,
            ...crossFilters,
            ...crossTabDrilldownFor("legs", componentDrilldownParams),
            component_color: legsColor,
            component_family: legsFamily,
            component_product_type: legsProductType,
            component_item: legsItem,
            door_color: doorColor,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        legsDeps
    );
    const topPanelsFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "top_panels" as const,
            ...crossFilters,
            ...crossTabDrilldownFor("top_panels", componentDrilldownParams),
            component_color: topPanelsColor,
            top_panel_size: topPanelSize,
            top_panel_kind: topPanelKind,
            component_family: topPanelsFamily,
            component_product_type: topPanelsProductType,
            component_item: topPanelsItem,
            door_color: doorColor,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        topPanelsDeps
    );

    const eketFetchParams = useMemo(
        () => ({
            anchor,
            country: countriesForApi,
            sub_market: subMarketsForApi,
            sales_channels: salesChannelsForApi,
            ...periodParams,
            component_type: "eket" as const,
            ...crossFilters,
            ...crossTabDrilldownFor("eket", componentDrilldownParams),
            component_color: eketColor,
            component_family: eketFamily,
            component_product_type: eketProductType,
            component_item: eketItem,
            door_color: doorColor,
            door_solution: doorSolution,
            frame_types: frameTypesForApi,
        }),
        eketDeps
    );

    const updateFilter = useCallback(
        (key: string, value: string | number | undefined) => {
            setSearchParams((prev) => {
                if (value === undefined || value === "" || value === null) {
                    prev.delete(key);
                } else {
                    prev.set(key, String(value));
                }
                return prev;
            });
        },
        [setSearchParams]
    );

    // Tab change handler - updates URL, omits "frames" as it's the default
    const setActiveTab = useCallback(
        (tab: string) => updateFilter("tab", tab === "frames" ? undefined : tab),
        [updateFilter]
    );

    // Cross-filter handlers
    const handleColorClick = useCallback((value: string) => updateFilter("color", value || undefined), [updateFilter]);

    const handleHeightClick = useCallback(
        (value: string) => {
            // Value is already toggled (comma-separated) from FramesDetails
            updateFilter("height_cm", value || undefined);
        },
        [updateFilter]
    );

    const handleDepthClick = useCallback(
        (value: string) => {
            updateFilter("depth_cm", value || undefined);
        },
        [updateFilter]
    );

    const handleWidthClick = useCallback(
        (widthCm: number | undefined) => {
            updateFilter("width_cm", widthCm);
        },
        [updateFilter]
    );

    const handleInteriorColorClick = useCallback(
        (value: string) => updateFilter("interior_color", value || undefined),
        [updateFilter]
    );

    const handleKnobsColorClick = useCallback(
        (value: string) => updateFilter("knobs_color", value || undefined),
        [updateFilter]
    );

    const handleInteriorFamilyClick = useCallback(
        (value: string) => updateFilter("interior_family", value || undefined),
        [updateFilter]
    );

    const handleLooseInteriorColorClick = useCallback(
        (value: string) => updateFilter("loose_interior_color", value || undefined),
        [updateFilter]
    );

    const handleLooseInteriorFamilyClick = useCallback(
        (value: string) => updateFilter("loose_interior_family", value || undefined),
        [updateFilter]
    );

    const handleLooseInteriorItemClick = useCallback(
        (value: string) => updateFilter("loose_interior_item", value || undefined),
        [updateFilter]
    );

    const handleInteriorProductTypeClick = useCallback(
        (value: string) => updateFilter("interior_product_type", value || undefined),
        [updateFilter]
    );

    const handleLooseInteriorProductTypeClick = useCallback(
        (value: string) => updateFilter("loose_interior_product_type", value || undefined),
        [updateFilter]
    );

    const handleKnobsProductTypeClick = useCallback(
        (value: string) => updateFilter("knobs_product_type", value || undefined),
        [updateFilter]
    );

    const handleLightingProductTypeClick = useCallback(
        (value: string) => updateFilter("lighting_product_type", value || undefined),
        [updateFilter]
    );

    const handleKnobsFamilyClick = useCallback(
        (value: string) => updateFilter("knobs_family", value || undefined),
        [updateFilter]
    );

    const handleLightingColorClick = useCallback(
        (value: string) => updateFilter("lighting_color", value || undefined),
        [updateFilter]
    );

    const handleLightingFamilyClick = useCallback(
        (value: string) => updateFilter("lighting_family", value || undefined),
        [updateFilter]
    );

    const handleInteriorItemClick = useCallback(
        (value: string) => updateFilter("interior_item", value || undefined),
        [updateFilter]
    );

    const handleKnobsItemClick = useCallback(
        (value: string) => updateFilter("knobs_item", value || undefined),
        [updateFilter]
    );

    const handleLightingItemClick = useCallback(
        (value: string) => updateFilter("lighting_item", value || undefined),
        [updateFilter]
    );

    const handleLegsColorClick = useCallback(
        (value: string) => updateFilter("legs_color", value || undefined),
        [updateFilter]
    );

    const handleLegsFamilyClick = useCallback(
        (value: string) => updateFilter("legs_family", value || undefined),
        [updateFilter]
    );

    const handleLegsProductTypeClick = useCallback(
        (value: string) => updateFilter("legs_product_type", value || undefined),
        [updateFilter]
    );

    const handleLegsItemClick = useCallback(
        (value: string) => updateFilter("legs_item", value || undefined),
        [updateFilter]
    );

    const handleTopPanelsColorClick = useCallback(
        (value: string) => updateFilter("top_panels_color", value || undefined),
        [updateFilter]
    );

    const handleTopPanelSizeClick = useCallback(
        (value: string) => updateFilter("top_panel_size", value || undefined),
        [updateFilter]
    );

    const handleTopPanelsFamilyClick = useCallback(
        (value: string) => updateFilter("top_panels_family", value || undefined),
        [updateFilter]
    );

    const handleTopPanelsProductTypeClick = useCallback(
        (value: string) => updateFilter("top_panels_product_type", value || undefined),
        [updateFilter]
    );

    const handleTopPanelsItemClick = useCallback(
        (value: string) => updateFilter("top_panels_item", value || undefined),
        [updateFilter]
    );

    const handleEketColorClick = useCallback(
        (value: string) => updateFilter("eket_color", value || undefined),
        [updateFilter]
    );

    const handleEketFamilyClick = useCallback(
        (value: string) => updateFilter("eket_family", value || undefined),
        [updateFilter]
    );

    const handleEketProductTypeClick = useCallback(
        (value: string) => updateFilter("eket_product_type", value || undefined),
        [updateFilter]
    );

    const handleEketItemClick = useCallback(
        (value: string) => updateFilter("eket_item", value || undefined),
        [updateFilter]
    );

    const handleDrawerFrontsProductTypeClick = useCallback(
        (value: string) => updateFilter("drawer_fronts_product_type", value || undefined),
        [updateFilter]
    );

    const handleRemoveFilter = useCallback(
        (key: FilterValue["key"]) => {
            if (key === "door_family") doorFamilyFromItemRef.current = false;
            if (key === "door_color") doorColorFromItemRef.current = false;
            if (key === "door_item") {
                // Clear door_item and any auto-set door_family/door_color
                setSearchParams((prev) => {
                    prev.delete("door_item");
                    if (doorFamilyFromItemRef.current) prev.delete("door_family");
                    if (doorColorFromItemRef.current) prev.delete("door_color");
                    return prev;
                });
                doorFamilyFromItemRef.current = false;
                doorColorFromItemRef.current = false;
                return;
            }
            updateFilter(key, undefined);
        },
        [updateFilter, setSearchParams]
    );

    const handleClearAllFilters = useCallback(() => {
        doorFamilyFromItemRef.current = false;
        doorColorFromItemRef.current = false;
        setSearchParams((prev) => {
            prev.delete("color");
            prev.delete("height_cm");
            prev.delete("depth_cm");
            prev.delete("width_cm");
            prev.delete("front_types");
            prev.delete("runner_types");
            prev.delete("door_color");
            prev.delete("door_solution");
            prev.delete("interior_color");
            prev.delete("knobs_color");
            prev.delete("interior_family");
            prev.delete("knobs_family");
            prev.delete("lighting_color");
            prev.delete("lighting_family");
            prev.delete("door_family");
            prev.delete("door_item");
            prev.delete("interior_item");
            prev.delete("interior_product_type");
            prev.delete("loose_interior_color");
            prev.delete("loose_interior_family");
            prev.delete("loose_interior_product_type");
            prev.delete("loose_interior_item");
            prev.delete("knobs_product_type");
            prev.delete("lighting_product_type");
            prev.delete("knobs_item");
            prev.delete("lighting_item");
            prev.delete("legs_color");
            prev.delete("legs_family");
            prev.delete("legs_product_type");
            prev.delete("legs_item");
            prev.delete("top_panels_color");
            prev.delete("top_panel_size");
            prev.delete("top_panel_kind");
            prev.delete("top_panels_family");
            prev.delete("top_panels_product_type");
            prev.delete("top_panels_item");
            prev.delete("drawer_fronts_color");
            prev.delete("drawer_fronts_family");
            prev.delete("drawer_fronts_product_type");
            prev.delete("drawer_fronts_item");
            prev.delete("frame_types");
            return prev;
        });
    }, [setSearchParams]);

    // Build active filters list for the ActiveFilters component
    const activeFilters: FilterValue[] = useMemo(() => {
        const filters: FilterValue[] = [];
        const colourChip = (value: string, combos?: { color_distribution?: { description: string }[] } | null) =>
            formatSelection(
                splitSelection(value, "|"),
                combos?.color_distribution?.map((c) => c.description),
                (v) => formatColourFilterValue(v),
                " | "
            );
        const familyChip = (value: string, combos?: { family_distribution?: { description: string }[] } | null) =>
            formatSelection(
                splitSelection(value),
                combos?.family_distribution?.map((f) => f.description)
            );
        const cmLabel = (v: string) => (v.toLowerCase() === "mixed" ? "Mixed" : `${v}cm`);
        const frameColourSource = pieDistributions.color.length > 0 ? pieDistributions.color : stats?.color_distribution;
        const frameColourUniverse = frameColourSource?.map((d) => (d.color ? (d.color === "Mixed" ? "mixed" : d.color) : "Unknown"));
        const cmUniverse = (items: { height_cm?: unknown; depth_cm?: unknown }[] | undefined, field: "height_cm" | "depth_cm") =>
            items?.map((d) => (d[field] === "Mixed" ? "mixed" : String(d[field] ?? "Unknown")));
        const frameHeightUniverse = cmUniverse(
            pieDistributions.height.length > 0 ? pieDistributions.height : stats?.height_distribution,
            "height_cm"
        );
        const frameDepthUniverse = cmUniverse(
            pieDistributions.depth.length > 0 ? pieDistributions.depth : stats?.depth_distribution,
            "depth_cm"
        );
        if (crossFilters.color) {
            filters.push({
                key: "color",
                label: "Frame colour",
                value: formatSelection(
                    splitSelection(crossFilters.color),
                    frameColourUniverse,
                    (v) => formatColourFilterValue(v)
                ),
            });
        }
        if (crossFilters.height_cm) {
            const hLabel = formatSelection(splitSelection(crossFilters.height_cm), frameHeightUniverse, cmLabel);
            filters.push({ key: "height_cm", label: "Height", value: hLabel });
        }
        if (crossFilters.depth_cm) {
            const dLabel = formatSelection(splitSelection(crossFilters.depth_cm), frameDepthUniverse, cmLabel);
            filters.push({ key: "depth_cm", label: "Depth", value: dLabel });
        }
        if (crossFilters.width_cm) {
            filters.push({ key: "width_cm", label: "Width", value: `${crossFilters.width_cm}cm` });
        }
        if (searchParams.has("front_types")) {
            filters.push({
                key: "front_types",
                label: "Front types",
                value: formatSelection(frontTypeSelection, Object.keys(FRONT_TYPE_LABELS), frontTypeLabelForValue),
            });
        }
        if (searchParams.has("runner_types")) {
            filters.push({
                key: "runner_types",
                label: "Runner types",
                value: formatSelection(
                    runnerTypeSelection,
                    frontCombos?.runner_type_distribution?.map((r) => runnerTypeValueForLabel(r.description)),
                    runnerTypeLabelForValue
                ),
            });
        }
        if (doorColor && !doorColorFromItemRef.current && !doorItem) {
            filters.push({
                key: "door_color",
                label: "Door colour",
                value: colourChip(doorColor, frontCombos),
            });
        }
        if (doorSolution) {
            const displayValue = formatSelection(
                splitSelection(doorSolution),
                fullDoorSolutions.map((d) => DOOR_SOLUTION_API_VALUES[d.label] ?? d.label),
                (v) => DOOR_SOLUTION_LABELS[v] || v
            );
            filters.push({ key: "door_solution", label: "Door Solution", value: displayValue });
        }
        if (interiorColor) {
            filters.push({
                key: "interior_color",
                label: "Interior colour",
                value: colourChip(interiorColor, interiorCombos),
            });
        }
        if (knobsColor) {
            filters.push({
                key: "knobs_color",
                label: "Knobs colour",
                value: colourChip(knobsColor, knobsCombos),
            });
        }
        if (interiorFamily) {
            filters.push({ key: "interior_family", label: "Interior Family", value: familyChip(interiorFamily, interiorCombos) });
        }
        if (interiorProductType) {
            filters.push({ key: "interior_product_type", label: "Interior Type", value: interiorProductType });
        }
        if (looseInteriorColor) {
            filters.push({
                key: "loose_interior_color",
                label: "Loose interior colour",
                value: colourChip(looseInteriorColor, looseInteriorCombos),
            });
        }
        if (looseInteriorFamily) {
            filters.push({ key: "loose_interior_family", label: "Loose Interior Family", value: familyChip(looseInteriorFamily, looseInteriorCombos) });
        }
        if (looseInteriorProductType) {
            filters.push({
                key: "loose_interior_product_type",
                label: "Loose Interior Type",
                value: looseInteriorProductType,
            });
        }
        if (knobsFamily) {
            filters.push({ key: "knobs_family", label: "Knobs Family", value: familyChip(knobsFamily, knobsCombos) });
        }
        if (knobsProductType) {
            filters.push({ key: "knobs_product_type", label: "Knobs Type", value: knobsProductType });
        }
        if (lightingColor) {
            filters.push({
                key: "lighting_color",
                label: "Lighting colour",
                value: colourChip(lightingColor, lightingCombos),
            });
        }
        if (lightingFamily) {
            filters.push({ key: "lighting_family", label: "Lighting Family", value: familyChip(lightingFamily, lightingCombos) });
        }
        if (lightingProductType) {
            filters.push({ key: "lighting_product_type", label: "Lighting Type", value: lightingProductType });
        }
        if (doorFamily && !doorFamilyFromItemRef.current && !doorItem) {
            filters.push({ key: "door_family", label: "Door Family", value: familyChip(doorFamily, frontCombos) });
        }
        if (doorItem) {
            filters.push({ key: "door_item", label: "Door Item", value: doorItem });
        }
        if (interiorItem) {
            filters.push({ key: "interior_item", label: "Interior Item", value: interiorItem });
        }
        if (looseInteriorItem) {
            filters.push({ key: "loose_interior_item", label: "Loose Interior Item", value: looseInteriorItem });
        }
        if (knobsItem) {
            filters.push({ key: "knobs_item", label: "Knobs Item", value: knobsItem });
        }
        if (lightingItem) {
            filters.push({ key: "lighting_item", label: "Lighting Item", value: lightingItem });
        }
        if (legsColor) {
            filters.push({
                key: "legs_color",
                label: "Legs colour",
                value: colourChip(legsColor, legsCombos),
            });
        }
        if (legsFamily) {
            filters.push({ key: "legs_family", label: "Legs Family", value: familyChip(legsFamily, legsCombos) });
        }
        if (legsProductType) {
            filters.push({ key: "legs_product_type", label: "Legs Type", value: legsProductType });
        }
        if (legsItem) {
            filters.push({ key: "legs_item", label: "Legs Item", value: legsItem });
        }
        if (topPanelsColor) {
            filters.push({
                key: "top_panels_color",
                label: "Top panel colour",
                value: colourChip(topPanelsColor, topPanelsCombos),
            });
        }
        if (topPanelSize) {
            filters.push({
                key: "top_panel_size",
                label: "Top Panel Size",
                value: topPanelSize.split("|").join(", "),
            });
        }
        if (topPanelKind) {
            filters.push({
                key: "top_panel_kind",
                label: "TV Panel",
                value: topPanelKind.split("|").join(", "),
            });
        }
        if (topPanelsFamily) {
            filters.push({ key: "top_panels_family", label: "Top Panel Family", value: familyChip(topPanelsFamily, topPanelsCombos) });
        }
        if (topPanelsProductType) {
            filters.push({ key: "top_panels_product_type", label: "Top Panel Type", value: topPanelsProductType });
        }
        if (topPanelsItem) {
            filters.push({ key: "top_panels_item", label: "Top Panel Item", value: topPanelsItem });
        }
        if (drawerFrontsColor) {
            filters.push({
                key: "drawer_fronts_color",
                label: "Drawer front colour",
                value: colourChip(drawerFrontsColor, frontCombos),
            });
        }
        if (drawerFrontsFamily) {
            filters.push({ key: "drawer_fronts_family", label: "Drawer Front Family", value: familyChip(drawerFrontsFamily, frontCombos) });
        }
        if (drawerFrontsProductType) {
            filters.push({
                key: "drawer_fronts_product_type",
                label: "Drawer Front Type",
                value: drawerFrontsProductType,
            });
        }
        if (drawerFrontsItem) {
            filters.push({ key: "drawer_fronts_item", label: "Drawer Front Item", value: drawerFrontsItem });
        }
        // Only show the frame_types pill when the user has explicitly chosen
        // a non-default selection. Otherwise removing the pill would just
        // restore the same default and the pill would not disappear.
        if (frameTypesForApi && searchParams.has("frame_types")) {
            const selected = frameTypesForApi.split(",").filter(Boolean);
            const labels: Record<string, string> = {
                regular: "Regular",
                corner: "Corner",
                end: "End",
                mid: "Mid",
                height_extension: "Height extension",
                tv: "TV bench",
            };
            filters.push({
                key: "frame_types",
                label: "Frame Types",
                value: formatSelection(selected, anchorConfig?.frame_type_categories, (t) => labels[t] || t),
            });
        }
        return filters;
    }, [
        crossFilters,
        frontTypeSelection,
        runnerTypeSelection,
        doorColor,
        doorSolution,
        interiorColor,
        knobsColor,
        interiorFamily,
        interiorProductType,
        looseInteriorColor,
        looseInteriorFamily,
        looseInteriorProductType,
        looseInteriorItem,
        knobsFamily,
        knobsProductType,
        lightingColor,
        lightingFamily,
        lightingProductType,
        doorFamily,
        doorItem,
        interiorItem,
        knobsItem,
        lightingItem,
        legsColor,
        legsFamily,
        legsProductType,
        legsItem,
        topPanelsColor,
        topPanelsFamily,
        topPanelsProductType,
        topPanelsItem,
        drawerFrontsColor,
        drawerFrontsFamily,
        drawerFrontsProductType,
        drawerFrontsItem,
        frameTypesForApi,
        searchParams,
        anchorConfig,
        fullDoorSolutions,
        pieDistributions,
        stats,
        frontCombos,
        interiorCombos,
        looseInteriorCombos,
        lightingCombos,
        knobsCombos,
        legsCombos,
        topPanelsCombos,
    ]);

    const looseInteriorFamilies = useMemo(
        () => componentFamilyNames(anchorConfig, "loose_interior", selectedCountries),
        [anchorConfig, selectedCountries],
    );

    // Tab configuration with attach rate badges from /v1/rates (fast, lightweight)
    // Tabs whose feature flag isn't configured for the current anchor are hidden.
    // "frames" is always shown. "loose-interior" piggy-backs on has_interior.
    const tabs: Tab[] = useMemo(() => {
        const flags = anchorFlags;
        const showIf = (flag: string) => flags.size === 0 || flags.has(flag);
        const all: (Tab & { requires?: string; requiresAny?: string[] })[] = [
            { id: "frames", label: "Frames", tooltip: `${anchor} frame dimensions, colours, and types` },
            {
                id: "fronts",
                label: frontsTabLabel,
                badge: formatAttachBadge(rates?.fronts),
                badgeLoading: ratesLoading,
                badgeError: !!ratesError,
                tooltip: showDoorSolutionsPie
                    ? `Doors bought with ${anchor} frames`
                    : `Doors and drawer fronts bought with ${anchor} frames`,
                requiresAny: ["has_doors", "has_drawer_fronts"],
            },
            {
                id: "interior",
                label: "Fixed Interior",
                badge: formatAttachBadge(interiorCombos?.attach_rate ?? rates?.interior),
                badgeLoading: interiorCombos ? interiorLoading : ratesLoading,
                badgeError: !interiorCombos && !!ratesError,
                tooltip:
                    anchor === "PAX"
                        ? `KOMPLEMENT fixed interior (shelves, drawers, rails) bought with ${anchor} frames`
                        : `Fixed interior (shelves, drawer frames, drawer runners) bought with ${anchor} frames`,
                requires: "has_interior",
            },
            {
                id: "loose-interior",
                label: "Loose Interior",
                badge: formatAttachBadge(looseInteriorCombos?.attach_rate ?? rates?.loose_interior),
                badgeLoading: looseInteriorCombos ? looseInteriorLoading : ratesLoading,
                badgeError: !looseInteriorCombos && !!ratesError,
                tooltip: withFamilies(`Loose storage bought with ${anchor} frames`, looseInteriorFamilies),
                requires: "has_interior",
            },
            {
                id: "knobs-handles",
                label: "Knobs & Handles",
                badge: formatAttachBadge(knobsCombos?.attach_rate ?? rates?.knobs_handles),
                badgeLoading: knobsCombos ? knobsLoading : ratesLoading,
                badgeError: !knobsCombos && !!ratesError,
                tooltip: `Knobs and handles bought with ${anchor} frames`,
                requires: "has_knobs_handles",
            },
            {
                id: "legs",
                label: "Legs",
                badge: formatAttachBadge(legsCombos?.attach_rate ?? rates?.legs),
                badgeLoading: legsCombos ? legsLoading : ratesLoading,
                badgeError: !legsCombos && !!ratesError,
                tooltip: `Legs bought with ${anchor} units`,
                requires: "has_legs",
            },
            {
                id: "top-panels",
                label: "Top Panels",
                badge: formatAttachBadge(topPanelsCombos?.attach_rate ?? rates?.top_panels),
                badgeLoading: topPanelsCombos ? topPanelsLoading : ratesLoading,
                badgeError: !topPanelsCombos && !!ratesError,
                tooltip: `Top panels bought with ${anchor} units`,
                requires: "has_top_panel",
            },
            {
                id: "lighting",
                label: "Lighting",
                badge: formatAttachBadge(lightingCombos?.attach_rate ?? rates?.lighting),
                badgeLoading: lightingCombos ? lightingLoading : ratesLoading,
                badgeError: !lightingCombos && !!ratesError,
                tooltip: `Lighting accessories bought with ${anchor} frames`,
                requires: "has_lighting",
            },
            {
                id: "eket",
                label: "EKET",
                badge: formatAttachBadge(eketCombos?.attach_rate ?? rates?.eket),
                badgeLoading: eketCombos ? eketLoading : ratesLoading,
                badgeError: !eketCombos && !!ratesError,
                tooltip: `EKET units bought with ${anchor} combinations`,
                requires: "has_eket",
            },
        ];
        return all
            .filter(
                (t) =>
                    (!t.requires || showIf(t.requires)) &&
                    (!t.requiresAny || anchorFlags.size === 0 || t.requiresAny.some((flag) => anchorFlags.has(flag)))
            )
            .map(({ requires: _r, requiresAny: _ra, ...rest }) => rest);
    }, [
        anchor,
        anchorFlags,
        frontsTabLabel,
        showDoorSolutionsPie,
        rates,
        ratesLoading,
        ratesError,
        interiorCombos,
        interiorLoading,
        looseInteriorCombos,
        looseInteriorLoading,
        looseInteriorFamilies,
        knobsCombos,
        knobsLoading,
        legsCombos,
        legsLoading,
        topPanelsCombos,
        topPanelsLoading,
        eketCombos,
        eketLoading,
        lightingCombos,
        lightingLoading,
    ]);

    // If the active tab is hidden for the current anchor, fall back to "frames".
    useEffect(() => {
        if (!anchorConfig) return;
        if (activeTab !== "frames" && !tabs.some((t) => t.id === activeTab)) {
            setActiveTab("frames");
        }
    }, [anchorConfig, activeTab, tabs, setActiveTab]);

    // Wait for the anchor config before rendering — it determines which tabs,
    // filters and pills are valid for this anchor (e.g. PAX has frame-type
    // categories, BESTÅ does not). Rendering before it loads causes brief
    // flashes of PAX-only widgets when the active anchor is BESTÅ.
    // Also wait until the loaded config matches the active anchor: useApi
    // keeps the previous data while refetching, so on PAX→BESTÅ the gates
    // would briefly use PAX flags and fire BESTÅ requests that 404.
    if (!anchorConfig || anchorConfig.anchor !== anchor) {
        return (
            <div className={styles.dashboard}>
                <LoadingSpinner message="Loading…" />
            </div>
        );
    }

    // No data for this anchor/country — show a friendly message
    // instead of an empty dashboard with infinite loading spinners.
    if (statsError && !statsLoading && !stats && !displayedBudgetEstimate && !queryBudgetUnavailable && !coverageError) {
        return (
            <div className={styles.dashboard}>
                <p style={{ textAlign: "center", marginTop: "3rem", color: "var(--colour-text-and-icon-2, #666)" }}>
                    No data available yet for <strong>{anchor}</strong> / {selectedCountries.join(", ")}.
                </p>
            </div>
        );
    }

    return (
        <div
            className={`${styles.dashboard} ${
                (!filtersReady || displayedBudgetEstimate || coverageError) && backendReady ? styles.staleResults : ""
            }`}
        >
            {/* Top bar with filters (anchor selector leads the bar) */}
            <div className={`${styles.topBar} ${isMobile ? styles.topBarMobile : ""}`}>
                {isMobile && (
                    <button
                        type="button"
                        className={styles.filterToggle}
                        onClick={() => setFiltersOpen((o) => !o)}
                        aria-expanded={filtersOpen}
                    >
                        <span>Filters</span>
                        <span className={styles.filterChevron} aria-hidden="true">{filtersOpen ? "▲" : "▼"}</span>
                    </button>
                )}
                {(!isMobile || filtersOpen) && (
                <FilterBar
                    leading={<AnchorSelector />}
                    selectedSubMarkets={selectedSubMarkets}
                    countries={countries || []}
                    selectedCountries={selectedCountries}
                    salesChannels={ALL_SALES_CHANNELS}
                    selectedSalesChannels={selectedSalesChannels}
                    period={period}
                    subMarkets={subMarkets || []}
                    favoriteCountry={favoriteCountry || undefined}
                    dataAvailableFrom={anchorConfig?.data_available_from || undefined}
                    onSubMarketsChange={(selected) => {
                        setSearchParams((prev) => {
                            if (selected.length === allSubMarketCodes.length) {
                                prev.delete("sub_markets");
                            } else {
                                prev.set("sub_markets", selected.join(","));
                            }
                            return prev;
                        });
                    }}
                    onCountriesChange={(selected) => {
                        // Update countries and reset stores/sub markets in a single state update
                        setSearchParams((prev) => {
                            // Remove old "country" param, use "countries" for multi-select
                            prev.delete("country");
                            prev.set("countries", selected.join(","));
                            prev.delete("sub_markets");
                            return prev;
                        });
                    }}
                    onSalesChannelsChange={(selected) => {
                        // Update sales channels - if all selected, remove param (default)
                        setSearchParams((prev) => {
                            if (selected.length === ALL_SALES_CHANNELS.length) {
                                prev.delete("sales_channels");
                            } else {
                                prev.set("sales_channels", selected.join(","));
                            }
                            return prev;
                        });
                    }}
                    onPeriodChange={(v) => updateFilter("period", v)}
                    onFavoriteCountryChange={handleFavoriteChange}
                    favoritePeriod={favoritePeriod || undefined}
                    onFavoritePeriodChange={handleFavoritePeriodChange}
                    {...(anchorConfig && anchorConfig.frame_type_categories.length === 0
                        ? {}
                        : {
                              frameTypes: effectiveFrameTypes,
                              frameTypeOptions: anchorConfig?.frame_type_categories,
                              defaultFrameTypes:
                                  anchorConfig?.default_frame_types ?? anchorConfig?.frame_type_categories,
                              onFrameTypesChange: (types: string[]) => {
                                  setSearchParams((prev) => {
                                      const allCats = anchorConfig?.frame_type_categories || ALL_FRAME_TYPES;
                                      const defaults = anchorConfig?.default_frame_types ?? allCats;
                                      const isDefault =
                                          types.length === 0 ||
                                          (types.length === defaults.length &&
                                              defaults.every((d) => types.includes(d)));
                                      if (isDefault) {
                                          prev.delete("frame_types");
                                      } else {
                                          prev.set("frame_types", types.join(","));
                                      }
                                      // Clear frame cross-filters when frame type filter changes
                                      prev.delete("color");
                                      prev.delete("height_cm");
                                      prev.delete("depth_cm");
                                      prev.delete("width_cm");
                                      return prev;
                                  });
                              },
                          })}
                    countryCounts={filterCounts?.countries}
                    subMarketCounts={filterCounts?.sub_markets}
                    channelCounts={filterCounts?.channels}
                    frameTypeCounts={frameTypeCounts}
                    countsLoading={filterCountsLoading}
                />
                )}
            </div>

            {(displayedBudgetEstimate || queryBudgetUnavailable) && (
                <div className={styles.budgetNotice}>
                    <QueryBudgetNotice
                        estimate={displayedBudgetEstimate}
                        error={queryBudgetUnavailable ? queryEstimateError : null}
                    />
                </div>
            )}

            {coverageError && (
                <div className={styles.budgetNotice}>
                    <CoverageNotice anchor={anchor} coverage={coverageError} />
                </div>
            )}

            {/* Country summary panel - shows aggregated values per country */}
            <CountrySummary
                data={countrySummary}
                loading={countrySummaryLoading}
                anchor={anchor}
                showInterior={anchorFlags.size === 0 || anchorFlags.has("has_interior")}
                expectedRows={selectedCountries.length}
            />

            {/* Active cross-filters */}
            <ActiveFilters filters={activeFilters} onRemove={handleRemoveFilter} onClearAll={handleClearAllFilters} />

            {/* Global stats bar - visible across all tabs */}
            {(ratesLoading || rates || stats) && (
                <div className={styles.globalStats}>
                    <div
                        className={styles.totalCombo}
                        title={`Total unique ${anchor} combinations sold in the selected period and markets`}
                    >
                        {!ratesLoading && (rates || stats) ? (
                            <>
                                <span className={styles.totalValue}>
                                    {(rates?.total_combinations ?? stats?.total_combinations)?.toLocaleString()}
                                </span>
                                <span className={styles.totalLabel}>combinations</span>
                            </>
                        ) : (
                            <>
                                <span className={styles.totalSpinner} />
                                <span className={styles.totalLabel}>combinations</span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* KPI badges: per-component attach rates */}
            <div className={styles.statsCards}>
                {(anchorFlags.size === 0 ||
                    anchorFlags.has("has_doors") ||
                    anchorFlags.has("has_drawer_fronts")) && (
                    <StatsCard
                        title={`${frontsTabLabel} Rate`}
                        value={rates ? `${rates.fronts.percentage.toFixed(1)}%` : "—"}
                        subtitle={rates ? `${rates.fronts.count.toLocaleString()} combos` : undefined}
                        tooltip={
                            showDoorSolutionsPie
                                ? `Percentage of ${anchor} combinations that include at least one door`
                                : `Percentage of ${anchor} combinations that include at least one door or drawer front`
                        }
                        loading={ratesLoading}
                        error={!!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("fronts")}
                    />
                )}
                {(anchorFlags.size === 0 || anchorFlags.has("has_interior")) && (
                    <StatsCard
                        title="Fixed Interior Rate"
                        value={
                            interiorCombos
                                ? `${interiorCombos.attach_rate.percentage.toFixed(1)}%`
                                : rates
                                  ? `${rates.interior.percentage.toFixed(1)}%`
                                  : "—"
                        }
                        subtitle={
                            interiorCombos
                                ? `${interiorCombos.attach_rate.count.toLocaleString()} combos`
                                : rates
                                  ? `${rates.interior.count.toLocaleString()} combos`
                                  : undefined
                        }
                        tooltip={`Percentage of ${anchor} combinations that include fixed KOMPLEMENT interior`}
                        loading={interiorCombos ? interiorLoading : ratesLoading}
                        error={!interiorCombos && !!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("interior")}
                    />
                )}
                {(anchorFlags.size === 0 || anchorFlags.has("has_interior")) && (
                    <StatsCard
                        title="Loose Interior Rate"
                        value={
                            looseInteriorCombos
                                ? `${looseInteriorCombos.attach_rate.percentage.toFixed(1)}%`
                                : rates
                                  ? `${rates.loose_interior.percentage.toFixed(1)}%`
                                  : "—"
                        }
                        subtitle={
                            looseInteriorCombos
                                ? `${looseInteriorCombos.attach_rate.count.toLocaleString()} combos`
                                : rates
                                  ? `${rates.loose_interior.count.toLocaleString()} combos`
                                  : undefined
                        }
                        tooltip={withFamilies(`Percentage of ${anchor} combinations that include loose storage`, looseInteriorFamilies)}
                        loading={looseInteriorCombos ? looseInteriorLoading : ratesLoading}
                        error={!looseInteriorCombos && !!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("loose-interior")}
                    />
                )}
                {(anchorFlags.size === 0 || anchorFlags.has("has_knobs_handles")) && (
                    <StatsCard
                        title="Knobs & Handles Rate"
                        value={
                            knobsCombos
                                ? `${knobsCombos.attach_rate.percentage.toFixed(1)}%`
                                : rates
                                  ? `${rates.knobs_handles.percentage.toFixed(1)}%`
                                  : "—"
                        }
                        subtitle={
                            knobsCombos
                                ? `${knobsCombos.attach_rate.count.toLocaleString()} combos`
                                : rates
                                  ? `${rates.knobs_handles.count.toLocaleString()} combos`
                                  : undefined
                        }
                        tooltip={`Percentage of ${anchor} combinations that include knobs or handles`}
                        loading={knobsCombos ? knobsLoading : ratesLoading}
                        error={!knobsCombos && !!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("knobs-handles")}
                    />
                )}
                {(anchorFlags.size === 0 || anchorFlags.has("has_legs")) && (
                    <StatsCard
                        title="Legs Rate"
                        value={
                            legsCombos
                                ? `${legsCombos.attach_rate.percentage.toFixed(1)}%`
                                : rates
                                  ? `${rates.legs.percentage.toFixed(1)}%`
                                  : "—"
                        }
                        subtitle={
                            legsCombos
                                ? `${legsCombos.attach_rate.count.toLocaleString()} combos`
                                : rates
                                  ? `${rates.legs.count.toLocaleString()} combos`
                                  : undefined
                        }
                        tooltip={`Percentage of ${anchor} combinations that include legs`}
                        loading={legsCombos ? legsLoading : ratesLoading}
                        error={!legsCombos && !!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("legs")}
                    />
                )}
                {(anchorFlags.size === 0 || anchorFlags.has("has_top_panel")) && (
                    <StatsCard
                        title="Top Panels Rate"
                        value={
                            topPanelsCombos
                                ? `${topPanelsCombos.attach_rate.percentage.toFixed(1)}%`
                                : rates
                                  ? `${rates.top_panels.percentage.toFixed(1)}%`
                                  : "—"
                        }
                        subtitle={
                            topPanelsCombos
                                ? `${topPanelsCombos.attach_rate.count.toLocaleString()} combos`
                                : rates
                                  ? `${rates.top_panels.count.toLocaleString()} combos`
                                  : undefined
                        }
                        tooltip={`Percentage of ${anchor} combinations that include a top panel`}
                        loading={topPanelsCombos ? topPanelsLoading : ratesLoading}
                        error={!topPanelsCombos && !!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("top-panels")}
                    />
                )}
                {(anchorFlags.size === 0 || anchorFlags.has("has_lighting")) && (
                    <StatsCard
                        title="Lighting Rate"
                        value={
                            lightingCombos
                                ? `${lightingCombos.attach_rate.percentage.toFixed(1)}%`
                                : rates
                                  ? `${rates.lighting.percentage.toFixed(1)}%`
                                  : "—"
                        }
                        subtitle={
                            lightingCombos
                                ? `${lightingCombos.attach_rate.count.toLocaleString()} combos`
                                : rates
                                  ? `${rates.lighting.count.toLocaleString()} combos`
                                  : undefined
                        }
                        tooltip={`Percentage of ${anchor} combinations that include lighting accessories`}
                        loading={lightingCombos ? lightingLoading : ratesLoading}
                        error={!lightingCombos && !!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("lighting")}
                    />
                )}
                {(anchorFlags.size === 0 || anchorFlags.has("has_eket")) && (
                    <StatsCard
                        title="EKET Rate"
                        value={
                            eketCombos
                                ? `${eketCombos.attach_rate.percentage.toFixed(1)}%`
                                : rates
                                  ? `${rates.eket.percentage.toFixed(1)}%`
                                  : "—"
                        }
                        subtitle={
                            eketCombos
                                ? `${eketCombos.attach_rate.count.toLocaleString()} combos`
                                : rates
                                  ? `${rates.eket.count.toLocaleString()} combos`
                                  : undefined
                        }
                        tooltip={`Percentage of ${anchor} combinations that include an EKET unit`}
                        loading={eketCombos ? eketLoading : ratesLoading}
                        error={!eketCombos && !!ratesError}
                        onRetry={ratesRefetch}
                        onClick={() => setActiveTab("eket")}
                    />
                )}
            </div>

            {/* Tab navigation + content */}
            <TabNav tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab}>
                {activeTab === "frames" && (
                    <>
                        {statsLoading ? (
                            <LoadingSpinner message="Loading..." />
                        ) : stats ? (
                            <FramesDetails
                                stats={stats}
                                widthDistribution={fullWidthDistribution}
                                widthLoading={statsLoading}
                                chartDistributions={{
                                    color: pieDistributions.color,
                                    height: pieDistributions.height,
                                    depth: pieDistributions.depth,
                                    frame_type: unfilteredFrameTypes.length > 0 ? unfilteredFrameTypes : undefined,
                                }}
                                crossFilters={crossFilters}
                                {...(anchorConfig && anchorConfig.frame_type_categories.length === 0
                                    ? {}
                                    : {
                                          frameTypes: effectiveFrameTypes,
                                          frameTypeOptions: anchorConfig?.frame_type_categories,
                                          onFrameTypeClick: (types: string[]) => {
                                              setSearchParams((prev) => {
                                                  const allCats =
                                                      anchorConfig?.frame_type_categories || ALL_FRAME_TYPES;
                                                  const defaults = anchorConfig?.default_frame_types ?? allCats;
                                                  const isDefault =
                                                      types.length === 0 ||
                                                      (types.length === defaults.length &&
                                                          defaults.every((d) => types.includes(d)));
                                                  if (isDefault) {
                                                      prev.delete("frame_types");
                                                  } else {
                                                      prev.set("frame_types", types.join(","));
                                                  }
                                                  prev.delete("color");
                                                  prev.delete("height_cm");
                                                  prev.delete("depth_cm");
                                                  prev.delete("width_cm");
                                                  return prev;
                                              });
                                          },
                                      })}
                                onColorClick={handleColorClick}
                                onHeightClick={handleHeightClick}
                                onDepthClick={handleDepthClick}
                                onWidthClick={handleWidthClick}
                            />
                        ) : null}
                    </>
                )}

                {activeTab === "fronts" && (
                    (anchorFlags.size === 0 || anchorFlags.has("has_doors") || anchorFlags.has("has_drawer_fronts")) && (
                        <section className={styles.frontSection}>
                            <ComponentSection
                                title={frontsTitleBase}
                                data={frontCombos}
                                loading={frontsLoading}
                                error={frontsError}
                                onRetry={frontsRefetch}
                                leadingFilter={
                                    showDoorSolutionsPie ? (
                                        <DistributionChart
                                            title="Door solutions"
                                            data={fullDoorSolutions}
                                            selectedValue={selectedDoorSolutionLabel}
                                            onSegmentClick={handleDoorSolutionClick}
                                        />
                                    ) : (
                                        <DistributionChart
                                            title="Front types"
                                            helpText={
                                                "Doors are combinations whose fronts are all hinged doors. Drawer fronts are combinations whose fronts are all drawer fronts. Mixed combines both. Front type comes from the eRIX product type; ambiguous 38 cm BESTÅ panels count as drawer fronts when the combination has a 25 cm drawer frame, and as doors otherwise. Drawer frames are the box a drawer front mounts on, not drawer runners."
                                            }
                                            data={
                                                frontCombos?.front_type_distribution
                                                    ? frontTypeDistributionFromCombos(
                                                          frontCombos.front_type_distribution
                                                      )
                                                    : rates
                                                      ? buildFrontTypeDistribution(rates)
                                                      : []
                                            }
                                            selectedValue={frontTypePieSelection}
                                            onSegmentClick={handleFrontTypeClick}
                                        />
                                    )
                                }
                                selectedColor={frontColorFilter}
                                onColorClick={handleFrontColorClick}
                                selectedFamily={frontFamilyFilter}
                                onFamilyClick={handleFrontFamilyClick}
                                selectedProductType={
                                    frontTypeSelection.length === 1 && frontTypeSelection[0] === "drawer_fronts"
                                        ? drawerFrontsProductType
                                        : undefined
                                }
                                onProductTypeClick={
                                    frontTypeSelection.length === 1 && frontTypeSelection[0] === "drawer_fronts"
                                        ? handleDrawerFrontsProductTypeClick
                                        : undefined
                                }
                                runnerTypeDistribution={frontCombos?.runner_type_distribution}
                                selectedRunnerType={runnerTypePieSelection}
                                onRunnerTypeClick={handleRunnerTypeClick}
                                fetchParams={frontsFetchParams}
                                onGenericClick={undefined}
                                selectedGenericItem={undefined}
                            />
                        </section>
                    )
                )}

                {activeTab === "interior" && (
                    <ComponentSection
                        title="Fixed Interior"
                        data={interiorCombos}
                        loading={interiorLoading || (!interiorCombos && !interiorError)}
                        error={interiorError}
                        onRetry={interiorRefetch}
                        pageSize={15}
                        selectedColor={interiorColor}
                        onColorClick={handleInteriorColorClick}
                        selectedFamily={interiorFamily}
                        onFamilyClick={handleInteriorFamilyClick}
                        selectedProductType={interiorProductType}
                        onProductTypeClick={handleInteriorProductTypeClick}
                        showProductTypeChart
                        fetchParams={interiorFetchParams}
                        onGenericClick={handleInteriorItemClick}
                        selectedGenericItem={interiorItem}
                    />
                )}

                {activeTab === "loose-interior" && (
                    <ComponentSection
                        title="Loose Interior"
                        data={looseInteriorCombos}
                        loading={looseInteriorLoading || (!looseInteriorCombos && !looseInteriorError)}
                        error={looseInteriorError}
                        onRetry={looseInteriorRefetch}
                        pageSize={15}
                        selectedColor={looseInteriorColor}
                        onColorClick={handleLooseInteriorColorClick}
                        selectedFamily={looseInteriorFamily}
                        onFamilyClick={handleLooseInteriorFamilyClick}
                        selectedProductType={looseInteriorProductType}
                        onProductTypeClick={handleLooseInteriorProductTypeClick}
                        showProductTypeChart
                        fetchParams={looseInteriorFetchParams}
                        onGenericClick={handleLooseInteriorItemClick}
                        selectedGenericItem={looseInteriorItem}
                    />
                )}

                {activeTab === "knobs-handles" && (
                    <ComponentSection
                        title="Knobs & Handles"
                        data={knobsCombos}
                        loading={knobsLoading || (!knobsCombos && !knobsError)}
                        error={knobsError}
                        onRetry={knobsRefetch}
                        pageSize={15}
                        selectedColor={knobsColor}
                        onColorClick={handleKnobsColorClick}
                        selectedFamily={knobsFamily}
                        onFamilyClick={handleKnobsFamilyClick}
                        selectedProductType={knobsProductType}
                        onProductTypeClick={handleKnobsProductTypeClick}
                        showProductTypeChart
                        fetchParams={knobsFetchParams}
                        onGenericClick={handleKnobsItemClick}
                        selectedGenericItem={knobsItem}
                    />
                )}

                {activeTab === "legs" && (
                    <ComponentSection
                        title="Legs"
                        data={legsCombos}
                        loading={legsLoading || (!legsCombos && !legsError)}
                        error={legsError}
                        onRetry={legsRefetch}
                        pageSize={15}
                        selectedColor={legsColor}
                        onColorClick={handleLegsColorClick}
                        selectedFamily={legsFamily}
                        onFamilyClick={handleLegsFamilyClick}
                        selectedProductType={legsProductType}
                        onProductTypeClick={handleLegsProductTypeClick}
                        showProductTypeChart
                        fetchParams={legsFetchParams}
                        onGenericClick={handleLegsItemClick}
                        selectedGenericItem={legsItem}
                    />
                )}

                {activeTab === "top-panels" && (
                    <ComponentSection
                        title="Top Panels"
                        data={topPanelsCombos}
                        loading={topPanelsLoading || (!topPanelsCombos && !topPanelsError)}
                        error={topPanelsError}
                        onRetry={topPanelsRefetch}
                        pageSize={15}
                        selectedColor={topPanelsColor}
                        onColorClick={handleTopPanelsColorClick}
                        selectedTopPanelSize={topPanelSize}
                        onTopPanelSizeClick={handleTopPanelSizeClick}
                        selectedFamily={topPanelsFamily}
                        onFamilyClick={handleTopPanelsFamilyClick}
                        selectedProductType={topPanelsProductType}
                        onProductTypeClick={handleTopPanelsProductTypeClick}
                        showProductTypeChart
                        fetchParams={topPanelsFetchParams}
                        onGenericClick={handleTopPanelsItemClick}
                        selectedGenericItem={topPanelsItem}
                    />
                )}

                {activeTab === "eket" && (
                    <ComponentSection
                        title="EKET"
                        data={eketCombos}
                        loading={eketLoading || (!eketCombos && !eketError)}
                        error={eketError}
                        onRetry={eketRefetch}
                        pageSize={15}
                        selectedColor={eketColor}
                        onColorClick={handleEketColorClick}
                        selectedFamily={eketFamily}
                        onFamilyClick={handleEketFamilyClick}
                        selectedProductType={eketProductType}
                        onProductTypeClick={handleEketProductTypeClick}
                        fetchParams={eketFetchParams}
                        onGenericClick={handleEketItemClick}
                        selectedGenericItem={eketItem}
                        showProductTypeChart
                    />
                )}

                {activeTab === "lighting" && (
                    <ComponentSection
                        title="Lighting"
                        data={lightingCombos}
                        loading={lightingLoading || (!lightingCombos && !lightingError)}
                        error={lightingError}
                        onRetry={lightingRefetch}
                        pageSize={15}
                        selectedColor={lightingColor}
                        onColorClick={handleLightingColorClick}
                        selectedFamily={lightingFamily}
                        onFamilyClick={handleLightingFamilyClick}
                        selectedProductType={lightingProductType}
                        onProductTypeClick={handleLightingProductTypeClick}
                        fetchParams={lightingFetchParams}
                        onGenericClick={handleLightingItemClick}
                        selectedGenericItem={lightingItem}
                        showProductTypeChart
                    />
                )}
            </TabNav>
        </div>
    );
}
